import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'

export default function UserNoticesModal({ isOpen, notices = [], onClose, onMarkRead }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % notices.length)
  }, [notices.length])

  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + notices.length) % notices.length)
  }, [notices.length])

  useEffect(() => {
    if (!isOpen || notices.length <= 1 || isPaused) return
    const interval = setInterval(handleNext, 5000)
    return () => clearInterval(interval)
  }, [isOpen, notices.length, isPaused, handleNext])

  useEffect(() => {
    if (currentIndex >= notices.length) {
      setCurrentIndex(Math.max(0, notices.length - 1))
    }
  }, [notices.length, currentIndex])

  if (!isOpen || notices.length === 0) return null

  const notice = notices[currentIndex]
  if (!notice) return null

  const hasCta = notice.has_button && notice.button_text && notice.button_link
  const isInternalLink = (() => {
    try {
      const href = notice.button_link || ''
      if (href.startsWith('/')) return true
      const url = new URL(href, window.location.origin)
      const host = window.location.host
      return (
        url.host === host ||
        url.hostname === 'sofera.com.br' ||
        url.hostname.endsWith('.sofera.com.br')
      )
    } catch {
      return false
    }
  })()

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
      <div 
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl shadow-2xl overflow-hidden relative group"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[11px] uppercase tracking-widest text-slate-400">Aviso {notices.length > 1 ? `(${currentIndex + 1}/${notices.length})` : ''}</span>
              <h3 className="text-xl font-extrabold text-white mt-1">{notice.title}</h3>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition p-1"><X size={20} /></button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          {notice.image_url && (
            <img 
              src={notice.image_url} 
              alt="Capa" 
              className="w-full block transition-all duration-500" 
              style={{ aspectRatio: '2 / 1', objectFit: 'contain', backgroundColor: '#0f172a' }} 
            />
          )}

          {notices.length > 1 && (
            <>
              <button 
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-950/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-950"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-slate-950/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-950"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        <div className="p-6 space-y-4 max-h-[40vh] overflow-y-auto custom-scrollbar">
          {!notice.image_url && (
            <h4 className="text-xl font-extrabold text-white">{notice.title}</h4>
          )}
          <div className="prose prose-invert max-w-none text-slate-300" dangerouslySetInnerHTML={{ __html: notice.content || '' }} />
          {hasCta && (
            <div>
              <a
                href={notice.button_link}
                target={isInternalLink ? undefined : "_blank"}
                rel={isInternalLink ? undefined : "noopener noreferrer"}
                onClick={onClose}
                className="inline-block px-6 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold transition shadow-lg shadow-orange-500/20"
              >
                {notice.button_text}
              </a>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex gap-2 order-2 sm:order-1">
            {notices.length > 1 && notices.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-1.5 transition-all rounded-full ${idx === currentIndex ? 'w-8 bg-teal-500' : 'w-2 bg-slate-700 hover:bg-slate-600'}`}
                aria-label={`Ir para aviso ${idx + 1}`}
              />
            ))}
          </div>

          <div className="flex gap-3 order-1 sm:order-2 w-full sm:w-auto">
            <button 
              onClick={() => onMarkRead(notice.id)} 
              className="flex-1 sm:flex-none px-4 py-2 rounded bg-teal-600 hover:bg-teal-700 text-white font-bold transition whitespace-nowrap"
            >
              Marcar como lido
            </button>
            <button 
              onClick={onClose} 
              className="flex-1 sm:flex-none px-4 py-2 rounded bg-slate-800 text-white font-bold hover:bg-slate-700 transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
